import {useQuery} from "@apollo/client";
import {UTCDate} from "@date-fns/utc";
import {differenceInSeconds, parseISO, startOfMinute} from "date-fns";
import {useEffect, useRef} from "react";
import {Helmet} from "react-helmet-async";
import {FiArrowLeft} from "react-icons/fi";
import {Link, useParams, useSearchParams} from "react-router";

import {Alert} from "@/components/alert/Alert.tsx";
import {useDateRange} from "@/components/datePicker/DatePickerURLRangeParamsContext.ts";
import {Last30Days} from "@/components/datePicker/subComponents/DatePickerShortcutList/DatePickerShortcutList.utils.ts";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import ContentContainer from "@/components/layout/ContentContainer.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer";
import {useMediaQuery} from "@/lib/hooks/useMediaQuery.ts";
import {CheckDetailsResponse, GQL_CHECK_DETAILS} from "@/lib/queries/checks.ts";
import {StandardDateRange} from "@/lib/queries/reports.ts";
import {MonitoringServiceType} from "@/lib/types/checks";
import {CheckAnalysisActionsBar} from "@/pages/checks/analysis/actions/CheckAnalysisActionsBar.tsx";
import {CheckDetailsActions} from "@/pages/checks/analysis/actions/CheckDetailsActions.tsx";
import {CheckHeader} from "@/pages/checks/analysis/CheckHeader.tsx";
import {CheckOverview} from "@/pages/checks/analysis/overview/checktypes/CheckOverview.tsx";
import {ServicePendingBanner} from "@/pages/checks/analysis/ServicePendingBanner.tsx";
import {StatsHeader} from "@/pages/checks/analysis/statsHeader/StatsHeader.tsx";
import {PENDING_STATE_SECONDS} from "@/pages/checks/analysis/utils.ts";
import {NetworkAnalysisRunTest} from "@/pages/checks/components/runtest/network/NetworkAnalysisRunTest.tsx";
import {NetworkAnalysisRunTestModal} from "@/pages/checks/components/runtest/network/NetworkAnalysisRunTestModal.tsx";
import {useNetworkAnalysisRunTest} from "@/pages/checks/components/runtest/network/useNetworkAnalysisRunTest.ts";
import {getCheckCreatedMessage} from "@/pages/checks/utils.ts";
import {NotFoundPage} from "@/pages/website/NotFoundPage";

import styles from "./CheckDetailsContent.module.scss";

export const CheckDetailsContent = () => {
  const {id} = useParams();
  const [searchParams] = useSearchParams();
  const checkId = parseInt(id!);
  const {dateRange, fixedRangeOption, handleRangeChange} = useDateRange({
    fromKey: "start",
    toKey: "end",
    fixedRangeKey: "range",
    withDefault: Last30Days,
  });

  const searchEnd = searchParams.get("end");
  const queryEnd = searchEnd ? dateRange.to : startOfMinute(dateRange.to);

  const {data, loading, previousData} = useQuery<CheckDetailsResponse>(GQL_CHECK_DETAILS, {
    variables: {
      id: checkId,
      start: dateRange.from,
      end: queryEnd,
    },
  });

  const lastLoadedRange = useRef({from: dateRange.from, to: dateRange.to});
  useEffect(() => {
    if (!loading && data) {
      lastLoadedRange.current = {from: dateRange.from, to: dateRange.to};
    }
  }, [loading, data, dateRange.from, dateRange.to]);

  const chartStart = loading && previousData ? lastLoadedRange.current.from : dateRange.from;
  const chartEnd = loading && previousData ? lastLoadedRange.current.to : dateRange.to;

  const renderData = loading ? previousData : data;

  // Run Test on the analysis page (NETWORK only). Called unconditionally (hooks
  // rules) with a possibly-undefined service; the panel appears once a run starts.
  const runTestService = renderData?.service;
  const isNetworkCheck = runTestService?.monitoring_service_type === MonitoringServiceType.NETWORK;
  const analysisRunTest = useNetworkAnalysisRunTest(runTestService, isNetworkCheck);
  const isWideScreen = useMediaQuery("(min-width: 1600px)");
  const showRunTestPanel = isNetworkCheck && isWideScreen && analysisRunTest.state !== "idle";
  const showRunTestModal = isNetworkCheck && !isWideScreen;

  if (!renderData) {
    return loading ? <LoadingContainer /> : <NotFoundPage />;
  }

  const {service, latestDowntime, serviceDefinitions, stats, alerts} = renderData;
  if (serviceDefinitions.length === 0) {
    console.warn("service definitions could not be fetched");
  }
  const serviceDef = serviceDefinitions!.find((s) => s.service_type === service.monitoring_service_type)!;
  const topAlertMsg = getCheckCreatedMessage(service.monitoring_service_type, serviceDef.force_interval);
  const isNewCheck = differenceInSeconds(new UTCDate(), service.created_at) < PENDING_STATE_SECONDS;

  const showStatsHeader = service.monitoring_service_type !== MonitoringServiceType.CLOUDSTATUS;

  const onRangeChange = (newStart: Date | null, newEnd: Date | null, newFixedRange: StandardDateRange | null) => {
    handleRangeChange(
      newStart && newEnd ? {from: newStart, to: newEnd} : null,
      newFixedRange ? newFixedRange : undefined
    );
  };

  return (
    <ContentContainer
      title={
        <IconButton
          icon={FiArrowLeft}
          iconProps={{size: "large"}}
          variant="ghost"
          size="fitIcon"
          as={Link}
          to="/checks"
          aria-label="Back to Checks"
          title="Back to Checks"
        />
      }
      actionsBar={
        <CheckDetailsActions
          id={service.id}
          publicURL={service.public_check_details_url}
          checkType={service.monitoring_service_type}
          networkRunTest={
            isNetworkCheck
              ? {
                  onRun: analysisRunTest.handleRun,
                  running: analysisRunTest.state === "running",
                  ready: analysisRunTest.ready,
                }
              : undefined
          }
        />
      }
      subHeader={
        <>
          <div className="px-4 py-4 border-bottom">
            <CheckHeader
              service={service}
              start={dateRange.from}
              end={dateRange.to}
              fixedRange={fixedRangeOption?.value}
              onRangeChange={onRangeChange}
            />
          </div>
          <CheckAnalysisActionsBar service={service} />
        </>
      }
    >
      <Helmet>
        <title>Check Analysis: {service.name}</title>
      </Helmet>

      <div className="d-flex flex-column gap-4">
        {isNewCheck && (
          <Alert variant={"verification"} dismissible>
            {topAlertMsg}
          </Alert>
        )}

        <ServicePendingBanner
          created_at={service.created_at}
          state_is_up={service.cached_state_is_up}
          checkType={service.monitoring_service_type}
          forceInterval={serviceDef?.force_interval}
        />

        <div className="d-flex gap-4">
          <div className={`flex-grow-1 d-flex flex-column gap-4 ${styles.mainColumn}`}>
            {showStatsHeader && (
              <StatsHeader
                alerts={alerts}
                createdAt={service.created_at}
                downtimeSecs={stats.totals.downtime_secs}
                interval={service.msp_interval}
                isPaused={service.is_paused}
                isUnderMaintenance={service.is_under_maintenance}
                isUp={service.cached_state_is_up}
                lastDownAlertAt={service.cached_last_down_alert_at}
                latestDowntime={latestDowntime}
                outages={stats.totals.outages}
                supportsDowntime={service.supports_downtime_alerting}
                uptime={stats.totals.uptime_pct}
                uptimeSLA={parseFloat(service.msp_uptime_sla)}
              />
            )}

            <CheckOverview
              checkId={checkId}
              createdAt={parseISO(service.created_at)}
              start={chartStart}
              end={chartEnd}
              fixedRange={fixedRangeOption?.value}
              onRangeChange={onRangeChange}
              checkType={service.monitoring_service_type}
              cloudStatus={service.selected_third_party_service_component}
              thirdPartyGroup={service.third_party_group}
              thirdPartyMonitoringType={service.third_party_monitoring_type}
              intervals={stats.intervals}
              flattenAlerts={stats.flatten_alerts}
              flattenAlertsTotal={stats.flatten_alerts_total}
              locations={stats.available_locations}
              responseTimes={stats.location_response_times}
              dataType={serviceDef!.performance_data_type}
              days={service.cached_response_time}
              interval={service.msp_interval}
              is_paused={service.is_paused}
              cached_state_is_up={service.cached_state_is_up}
              downtime_secs={stats.totals.downtime_secs}
              outages={stats.totals.outages}
              latestDowntime={latestDowntime}
              uptimeBarEvents={stats.check_state_events}
            />
          </div>

          {showRunTestPanel && (
            <div className={styles.runTestPanel}>
              <NetworkAnalysisRunTest
                state={analysisRunTest.state}
                result={analysisRunTest.result}
                error={analysisRunTest.error}
                title={analysisRunTest.title}
                onRun={analysisRunTest.handleRun}
                ready={analysisRunTest.ready}
              />
            </div>
          )}
        </div>
      </div>

      {showRunTestModal && (
        <NetworkAnalysisRunTestModal
          state={analysisRunTest.state}
          result={analysisRunTest.result}
          error={analysisRunTest.error}
          title={analysisRunTest.title}
        />
      )}
    </ContentContainer>
  );
};
